import React from 'react';
import theme from '@/style';
import {
  get3D,
  getColor,
  getDice,
  getKerala,
  getQuick3D,
  getQuickStateOrders,
  getSatta,
  getScratchAndCasino,
  getSports,
  getStateOrders,
} from './bets.service';
import NoData from '@/components/basic/error-pages/no-data';
import {useTranslation} from 'react-i18next';
import globalStore from '@/services/global.state';
import BetsListItem from './bets-list-item';
import {formatDate, getDaysByDate} from '@/utils';
import Drawer, {DrawerRef} from '@/components/basic/game-drawer/game-drawer';
import SharePanel from '@/components/business/share-panel/share-panel';
import {shareToThird, shareToChat} from '@/common-pages/bets/bets.service';
import {IShareInfo} from '@/common-pages/bets/bets.service';
import {getShareAward} from '@/services/global.service';
import {TGameCode} from '@/services/global.service';
import {useShareToast} from '../hooks/shareToast.hooks';
import {getUUID, transfor} from '@/utils';
import {SafeAny} from '@/types';

const map: {[key: string]: TGameCode} = {
  Kerala: 'kerala',
  '3 Digit': '3Digit',
  'Quick 3D': 'quick3d',
  Color: 'color',
  Dice: 'dice',
  'Satta Matka': 'matka',
  'Quick Race': 'quickRace',
  'Scratch off': 'scratch',
  Casino: 'casino',
  Live: 'live',
  Sports: 'sport',
  'State Lottery': 'stateLottery',
  'Quick State Lottery': 'quickStateLottery',
};

const getOrderStatus = (gameName: string, status: string) => {
  if (status === 'ALL') {
    return '3';
  }
  if (
    [
      'Kerala',
      '3 Digit',
      'Quick 3D',
      'State Lottery',
      'Quick State Lottery',
    ].includes(gameName)
  ) {
    return status === 'UNKNOWN' ? '2' : status === 'KNOWN' ? '0' : '1';
  } else if (['Dice', 'Color'].includes(gameName)) {
    return status === 'UNKNOWN' ? '0' : status === 'KNOWN' ? '4' : '1';
  } else if (gameName === 'Satta Matka') {
    return status === 'UNKNOWN' ? '2' : status === 'KNOWN' ? '4' : '1';
  } else {
    return status === 'UNKNOWN' ? '0' : status === 'KNOWN' ? '4' : '1';
  }
};

const BetsList = (props: {
  currentDate?: Date;
  isActive?: boolean;
  status: string | 'ALL' | 'UNKNOWN' | 'KNOWN' | 'WON';
  game: string;
  scrollEnable?: boolean;
}) => {
  const {shareSuccess, shareFail} = useShareToast();
  const pageSize = 10;
  const pageRef = React.useRef(1);
  const hasMores = React.useRef(false);
  const [loading, setLoading] = React.useState(false);
  const [list, setList] = React.useState<SafeAny[]>([]);
  const [firstLoad, setFirstLoad] = React.useState(true);
  const [moreLoading, setMoreLoading] = React.useState(false);
  const [shareAward, setShareAward] = React.useState<number>(0);
  const panelRef = React.useRef<DrawerRef>(null);
  const [threeOrderData, setThreeOrderData] = React.useState<SafeAny>({});

  const {i18n} = useTranslation();
  const {status, game, currentDate, isActive = false, scrollEnable} = props;

  React.useEffect(() => {
    if (isActive) {
      setFirstLoad(true);
      getList(true, true).then();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game, status, currentDate, isActive]);

  const getList = async (refresh = false, isFirst = false) => {
    if (loading || moreLoading) {
      return;
    }
    if (refresh) {
      setList([]);
      pageRef.current = 1;
      if (!firstLoad && !isFirst) {
        setLoading(true);
      }
    } else {
      pageRef.current += 1;
      setMoreLoading(true);
    }
    const yearMonth = currentDate ? formatDate(currentDate, 'yyyyMM') : '';
    try {
      let res = [];
      let data;
      const normalParams = {
        orderStatus: getOrderStatus(game, status),
        pageNo: pageRef.current,
        pageSize,
        ...(yearMonth ? {yearMonth} : {}),
      };
      const startTime = currentDate
        ? formatDate(currentDate, 'yyyy-MM-01 00:00:00')
        : '';
      const endTime = currentDate
        ? formatDate(getDaysByDate(currentDate), 'yyyy-MM-dd 23:59:59')
        : '';
      const award = await getShareAward(map[game]);
      setShareAward(Math.floor(award));
      switch (game) {
        case 'Kerala':
          // 0:已开奖，1:已中奖，2:未开奖 3:全部
          data =
            (await getKerala({
              ...normalParams,
              // orderStatus
              lotteryType: 'KERALA',
            })) || [];
          res = data!.content || [];
          break;
        case '3 Digit':
          // 0:已开奖，1:已中奖，2:未开奖 3:全部
          data = (await get3D(normalParams)) || [];
          res = data!.content || [];
          break;
        case 'Quick 3D':
          data = await getQuick3D(normalParams);
          res = data!.records || [];
          break;
        case 'Color':
          // 0-未开奖 1-已中奖 2-未中奖 3全部
          data = await getColor(normalParams);
          res = data!.content || [];
          break;
        case 'Dice':
          // 0-未开奖 1-已中奖 2-未中奖 3全部
          data = await getDice(normalParams);
          res = data!.content || [];
          break;
        case 'Satta Matka':
          // 0未中奖1已中奖2未开奖 3全部
          res = (await getSatta(normalParams)) || [];
          break;
        case 'Quick Race':
        case 'Scratch off':
        case 'Casino':
        case 'Live':
          // 1=中奖 0=已使用 2=未使用 3全部
          const gameType =
            game === 'Scratch off'
              ? 1
              : game === 'Casino'
              ? 2
              : game === 'Quick Race'
              ? 5
              : 3;
          res =
            (await getScratchAndCasino({
              ...normalParams,
              ...(startTime ? {startTime} : {}),
              ...(endTime ? {endTime} : {}),
              gameType,
            })) || [];
          break;
        case 'Sports':
          // 0未中奖1已中奖2未开奖 3全部
          const result = await getSports({
            ...normalParams,
            ...(startTime ? {startTime} : {}),
            ...(endTime ? {endTime} : {}),
          });
          res = result.content || [];
          break;
        case 'State Lottery':
          data = await getStateOrders(normalParams);
          res = data.records || [];
          break;
        case 'Quick State Lottery':
          data = await getQuickStateOrders(normalParams);
          res = data.records || [];
          break;
      }
      setList(refresh ? res : list.concat(res));
      hasMores.current = res.length >= pageSize;
    } finally {
      setFirstLoad(false);
      setLoading(false);
      setMoreLoading(false);
    }
  };

  const onShare = (item: SafeAny) => {
    const user = localStorage.getItem('user') || '{}';
    const userId = JSON.parse(user).userId;
    setThreeOrderData({
      userId,
      data: transfor({...item, userId}, map[game], {
        from: 'order',
      }),
    });
    panelRef.current?.open();
  };

  const onLoadMore = async () => {
    if (hasMores.current) {
      await getList(false);
    }
  };

  const Footer = React.useMemo(() => {
    if (moreLoading) {
      return (
        <div style={stylesWeb.footer}>
          <span style={stylesWeb.loadingText}>Loading...</span>
        </div>
      );
    }
    if (!firstLoad && list.length > 0 && !hasMores.current) {
      return (
        <div style={stylesWeb.noMore}>
          <span style={stylesWeb.noMoreText}>{i18n.t('label.noMoreData')}</span>
        </div>
      );
    }
    return null;
  }, [firstLoad, i18n, list.length, moreLoading]);

  const onCopy = (id: string) => {
    navigator.clipboard?.writeText(id);
    globalStore.globalSucessTotal(i18n.t('copy-success'));
  };
  return (
    <div style={stylesWeb.wrap}>
      <div style={stylesWeb.loadingScope}>
        <div
          id={`bets-${status}`}
          style={{
            ...stylesWeb.list,
            overflowY: scrollEnable === false ? 'hidden' : 'auto',
          }}
          onScroll={e => {
            const target = e.currentTarget;
            if (
              target.scrollHeight - target.scrollTop - target.clientHeight <
              80
            ) {
              onLoadMore();
            }
          }}>
          {list.map((item, index) => (
            <div key={index}>
              <BetsListItem
                info={item}
                hasReward={Boolean(shareAward)}
                onShare={() => onShare(item)}
                onCopy={onCopy}
                canGoDetail
                onRefresh={() => getList(true)}
                game={game}
              />
            </div>
          ))}
          {!firstLoad && !loading && list.length === 0 ? (
            <div style={stylesWeb.empty}>
              <NoData />
            </div>
          ) : null}
          {Footer}
        </div>
        {firstLoad ? (
          <div style={stylesWeb.loading}>
            <div className="animate-spin" style={stylesWeb.spinner} />
          </div>
        ) : null}
      </div>
      <Drawer mode="bottom" ref={panelRef} contentBackgroundColor="transparent">
        <SharePanel
          gameType={map[game]}
          reward={shareAward}
          onClose={() => panelRef.current?.close()}
          onItemPress={pt => {
            panelRef.current?.close();
            shareToThird(
              pt,
              {
                gameCode: map[game],
                orderNo: getUUID(),
                threeOrderData,
              },
              (info: IShareInfo) => shareToChat(info),
              () => {
                shareSuccess(shareAward);
                setShareAward(0);
              },
              shareFail,
            );
          }}
        />
      </Drawer>
    </div>
  );
};

export default React.memo(BetsList);

const stylesWeb: Record<string, React.CSSProperties> = {
  wrap: {
    flex: 1,
    minHeight: 0,
    backgroundColor: theme.backgroundColor.lightGrey,
  },
  loadingScope: {
    height: '100%',
    minHeight: 0,
    position: 'relative',
  },
  list: {
    height: '100%',
    padding: '0 0.75rem',
    boxSizing: 'border-box',
  },
  empty: {
    minHeight: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  footer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '1rem',
  },
  loadingText: {
    color: '#9FA5AC',
    fontSize: '0.875rem',
  },
  noMore: {
    paddingTop: '1.5rem',
    paddingBottom: '4rem',
    textAlign: 'center',
  },
  noMoreText: {
    color: '#9FA5AC',
    fontSize: '0.875rem',
  },
  loading: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.13)',
  },
  spinner: {
    width: '2rem',
    height: '2rem',
    borderRadius: '9999px',
    border: '0.1875rem solid rgba(255, 255, 255, 0.7)',
    borderTopColor: theme.basicColor.primary,
  },
};
